import { useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Link, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@artstorefronts/ui';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { SOCIAL_BADGE_COUNTS_QUERY } from '~/graphql/queries';
import { ACCEPT_CONNECTION_REQUEST_MUTATION } from '~/graphql/connection';
import { UserAvatar } from '~/components/UserAvatar';
import { useAnalytics } from '~/context/analytics';
import { useAuth } from '~/context/auth';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useSayHiNudge } from '~/hooks/useSayHiNudge';
import { relativeTime } from '~/utils/dateTime';

const HIDDEN_KEY = 'ah:pending-requests-banner:hidden-id';

type Hidden = { viewerId: string | undefined; id: string | null };

type PendingRequest = {
  id: string;
  createdAt: string;
  requestedBy: { asfId: string; displayName?: string | null; slug?: string | null; avatarUrl?: string | null };
};

// Storage can be absent or throw (private window, blocked site data); the in-memory
// copy in state still hides the banner for the session.
// Keyed per viewer so a shared browser never hides one account's requests for another.
function readHiddenId(viewerId: string | undefined): string | null {
  if (!viewerId) return null;

  try {
    return window.localStorage.getItem(`${HIDDEN_KEY}:${viewerId}`);
  } catch {
    return null;
  }
}

function writeHiddenId(viewerId: string | undefined, id: string) {
  if (!viewerId) return;

  try {
    window.localStorage.setItem(`${HIDDEN_KEY}:${viewerId}`, id);
  } catch {
    // Session-only hide; state already holds it.
  }
}

// Connection ids are a BIGINT sequence and the list is ordered `id DESC`, so a newer
// request is exactly one whose id is larger than the hidden one.
function isHidden(newestId: string, hiddenId: string | null) {
  if (!hiddenId) return false;

  return Number(newestId) <= Number(hiddenId);
}

function isNetworkPath(pathname: string) {
  return pathname === '/network' || pathname.startsWith('/network/');
}

/**
 * Pending connection requests, newest first, at the top of every chrome'd client page
 * except /network/* (spec #4092). Reads the SocialBadgeCounts query dashboardLayout
 * already polls and NotificationToastListener refetches on a new request, so it costs no
 * request of its own and appears live. Owns the Say hi nudge so the dialog survives the
 * banner disappearing when the last request is accepted.
 */
export function PendingRequestsBanner() {
  const { pathname } = useLocation();
  const { profile } = useAuth();
  // asfId, same key town-square derives from Clerk publicMetadata.asfId.
  const viewerId = profile?.asfId ? String(profile.asfId) : undefined;
  const { enabled: connectionsEnabled } = useFeatureFlag('connections_and_dms');
  const { enabled: nudgesEnabled } = useFeatureFlag('connection_request_nudges');
  const enabled = connectionsEnabled && nudgesEnabled;
  const { data } = useQuery(SOCIAL_BADGE_COUNTS_QUERY, { fetchPolicy: 'cache-only', skip: !enabled });
  const [hidden, setHidden] = useState<Hidden>(() => ({ viewerId, id: readHiddenId(viewerId) }));
  // Account switch without a remount: re-read the new viewer's marker.
  if (hidden.viewerId !== viewerId) setHidden({ viewerId, id: readHiddenId(viewerId) });
  const hiddenId = hidden.viewerId === viewerId ? hidden.id : null;
  const { show: showSayHi, dialog: sayHiDialog } = useSayHiNudge();

  // Accepted ids stay out of the list until the cache catches up; the accept mutation does
  // not evict them and the post-accept refetch can fail.
  const [acceptedIds, setAcceptedIds] = useState<ReadonlySet<string>>(new Set());

  const allRequests: PendingRequest[] = (data?.incomingConnectionRequests?.edges ?? []).map((edge: any) => edge.node);
  const requests = allRequests.filter((request) => !acceptedIds.has(request.id));
  const total: number = (data?.incomingConnectionRequests?.totalCount ?? 0) - (allRequests.length - requests.length);
  const newest = requests[0];
  const visible = enabled && !isNetworkPath(pathname) && total > 0 && !!newest && !isHidden(newest.id, hiddenId);

  const accepted = (id: string) => setAcceptedIds((ids) => new Set(ids).add(id));

  const hide = (id: string) => {
    writeHiddenId(viewerId, id);
    setHidden({ viewerId, id });
  };

  return (
    <>
      {visible && (
        <BannerCard requests={requests} total={total} onHide={hide} onAccepted={accepted} showSayHi={showSayHi} />
      )}
      {sayHiDialog}
    </>
  );
}

function BannerCard({
  requests,
  total,
  onHide,
  onAccepted,
  showSayHi,
}: {
  requests: PendingRequest[];
  total: number;
  onHide: (id: string) => void;
  onAccepted: (id: string) => void;
  showSayHi: ReturnType<typeof useSayHiNudge>['show'];
}) {
  const { t, i18n } = useTranslation('social');
  const {
    amplitude: { track },
  } = useAnalytics();
  const client = useApolloClient();
  const [accept] = useMutation(ACCEPT_CONNECTION_REQUEST_MUTATION);
  const [accepting, setAccepting] = useState(false);
  const trackedId = useRef<string | null>(null);

  const newest = requests[0];
  const requester = newest.requestedBy;
  const name = requester.displayName || requester.slug || t('network.unnamed-user', 'Unnamed');
  const others = total - 1;
  const sentAgo = relativeTime(newest.createdAt, i18n.resolvedLanguage ?? i18n.language);

  useEffect(() => {
    if (trackedId.current === newest.id) return;
    trackedId.current = newest.id;
    track('pending_requests_banner_shown', { connection_id: newest.id, pending_count: total });
  }, [newest.id, total, track]);

  // The accept already landed or already failed; a refetch error is not the user's problem.
  const refetchCounts = async () => {
    try {
      await client.refetchQueries({ include: [SOCIAL_BADGE_COUNTS_QUERY] });
    } catch (err) {
      console.error('SocialBadgeCounts refetch failed:', err);
    }
  };

  const handleAccept = async () => {
    track('pending_requests_banner_accept_clicked', {
      connection_id: newest.id,
      requester_user_id: requester.asfId,
      pending_count: total,
    });
    setAccepting(true);
    try {
      await accept({ variables: { connectionId: newest.id } });
    } catch {
      // Raw server text is not user copy. Refetch so a withdrawn or blocked request drops
      // out instead of failing on every click.
      toast.error(t('pending-requests-banner.accept-error'));
      await refetchCounts();
      setAccepting(false);
      return;
    }

    toast.success(t('pending-requests-banner.accepted'));
    onAccepted(newest.id);
    showSayHi({
      recipientAsfId: requester.asfId,
      displayName: name,
      connectionId: newest.id,
      role: 'acceptor',
      source: 'pending_requests_banner',
    });
    // Stay disabled until the list drops the accepted request, or a second click hits "not pending".
    await refetchCounts();
    setAccepting(false);
  };

  const handleHide = () => {
    track('pending_requests_banner_hidden', { connection_id: newest.id, pending_count: total });
    onHide(newest.id);
  };

  return (
    <div className="flex-shrink-0 px-2 pt-2 md:px-4 md:pt-4">
      <section
        aria-label={t('pending-requests-banner.aria-label')}
        className="relative rounded-lg border border-gray-200 bg-white py-3 pl-3.5 pr-11 text-sm text-zinc-900"
      >
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
            <span className="flex shrink-0">
              {requests.map((request, index) => (
                <UserAvatar
                  key={request.id}
                  avatarUrl={request.requestedBy.avatarUrl}
                  displayName={request.requestedBy.displayName}
                  size={32}
                  className={`ring-2 ring-white ${index > 0 ? '-ml-2.5' : ''}`}
                />
              ))}
            </span>
            <div className="min-w-0">
              <p>
                <Trans
                  t={t}
                  i18nKey={others > 0 ? 'pending-requests-banner.title-many' : 'pending-requests-banner.title-one'}
                  values={others > 0 ? { name, count: others } : { name }}
                  components={{ b: <strong className="font-semibold" /> }}
                />
              </p>
              {sentAgo && (
                <p className="text-xs text-gray-500">{t('pending-requests-banner.sent', { time: sentAgo })}</p>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" className="rounded-full" disabled={accepting} onClick={handleAccept}>
              {t('pending-requests-banner.accept')}
            </Button>
            <Link to="/network/requests" className="px-2 text-sm font-medium text-zinc-900 hover:underline">
              {others > 0 ? t('pending-requests-banner.see-all', { total }) : t('pending-requests-banner.view-request')}
            </Link>
          </div>
        </div>
        <button
          type="button"
          onClick={handleHide}
          aria-label={t('pending-requests-banner.hide')}
          className="absolute right-2 top-2.5 inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-zinc-900 focus-visible:outline-2 focus-visible:outline-gray-400"
        >
          <X className="h-4 w-4" />
        </button>
      </section>
    </div>
  );
}
